import React from 'react';
import { Table, Button, Modal, Form, Input, Select, Space, Tag, Card, Empty, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';

function authHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function Users() {
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [modalVisible, setModalVisible] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState(null);
  const [form] = Form.useForm();

  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const isAdmin = currentUser.role === 'admin';

  React.useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users', { headers: authHeader() });
      setUsers(response.data.users || []);
    } catch {
      message.error('Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    form.setFieldsValue(user);
    setModalVisible(true);
  };

  const handleDeleteUser = (userId) => {
    if (userId === currentUser.id) { message.warning('No puedes eliminarte a ti mismo'); return; }
    Modal.confirm({
      title: 'Confirmar eliminación',
      content: '¿Eliminar este usuario? También se eliminarán sus dispositivos.',
      okText: 'Eliminar', okType: 'danger', cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await axios.delete(`/api/users/${userId}`, { headers: authHeader() });
          message.success('Usuario eliminado'); loadUsers();
        } catch { message.error('Error al eliminar usuario'); }
      }
    });
  };

  const handleSaveUser = async (values) => {
    try {
      if (editingUser) {
        const payload = { email: values.email, role: values.role, is_active: values.is_active };
        if (values.password) payload.password = values.password;
        await axios.put(`/api/users/${editingUser.id}`, payload, { headers: authHeader() });
        message.success('Usuario actualizado');
      } else {
        await axios.post('/api/users', values, { headers: authHeader() });
        message.success('Usuario creado');
      }
      loadUsers();
      setModalVisible(false);
    } catch (err) { message.error(err.response?.data?.error || 'Error al guardar usuario'); }
  };

  const columns = [
    {
      title: 'Usuario',
      dataIndex: 'username',
      key: 'username',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Rol',
      dataIndex: 'role',
      key: 'role',
      render: (role) => {
        const colors = {
          admin: 'red',
          user: 'default',
        };
        return <Tag color={colors[role] || 'default'}>{role.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Activo',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Sí' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Creado',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString('es-CL'),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => isAdmin ? (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEditUser(record)}>Editar</Button>
          {record.id !== currentUser.id && (
            <Button danger icon={<DeleteOutlined />} size="small" onClick={() => handleDeleteUser(record.id)}>Eliminar</Button>
          )}
        </Space>
      ) : null,
    },
  ];

  return (
    <div>
      <h1 style={{ marginBottom: 20 }}>👥 Gestión de Usuarios</h1>
      <Card>
        <Space style={{ marginBottom: 16 }}>
          {isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={handleAddUser}>Nuevo Usuario</Button>}
          <Button icon={<ReloadOutlined />} onClick={loadUsers} loading={loading}>Actualizar</Button>
        </Space>
        {users.length === 0
          ? <Empty description="No hay usuarios" />
          : <Table dataSource={users} columns={columns} rowKey="id" loading={loading} pagination={{ pageSize: 10 }} scroll={{ x: true }} />
        }
      </Card>

      <Modal title={editingUser ? 'Editar Usuario' : 'Nuevo Usuario'} open={modalVisible}
        onOk={() => form.submit()} onCancel={() => setModalVisible(false)} okText="Guardar">
        <Form form={form} layout="vertical" onFinish={handleSaveUser}>
          <Form.Item name="username" label="Usuario" rules={[{ required: !editingUser }]}>
            <Input disabled={!!editingUser} />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="password"
            label={editingUser ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña'}
            rules={editingUser ? [] : [{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="role" label="Rol" initialValue="user">
            <Select>
              <Select.Option value="user">Usuario</Select.Option>
              <Select.Option value="admin">Administrador</Select.Option>
            </Select>
          </Form.Item>
          {editingUser && (
            <Form.Item name="is_active" label="Estado" initialValue={1}>
              <Select>
                <Select.Option value={1}>Activo</Select.Option>
                <Select.Option value={0}>Bloqueado</Select.Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
